import { AUFTRAG_API } from '@Constants/backendConstants';
import { FEATURE_FLAGS } from '@Constants/featureFlagConstants';
import { useIsPortalSession } from '@Contexts/session';
import { usePortalContainerTypeLabels, usePortalServiceLabels } from '@Hooks/Stammdaten/usePortalLabels';
import { useRemoteCacheInvalidation } from '@Hooks/useRemoteCacheInvalidation';
import { type ApiRequisition } from '@Model';
import {
    EditFrameMode,
    FlexColumn,
    isLinked,
    FrameHeader,
    type IFrameBodyProps,
    LoadingFrame,
    ScrollContainer,
    UnsavedChangesModal,
    useTranslate,
    Wizard,
    type WizardStep,
    WizardSuccessScreen,
} from '@synqo/frontend-component-library';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, type Location, useBeforeUnload, useBlocker, useSearchParams } from 'react-router';
import { useClientNavigate } from '../../../../Routing/useClientNavigate';
import { buildWizardSteps, detailsStepIndexFor } from './buildWizardSteps';
import { countsSelection } from '@utils/requisitionAction';
import { WIZARD_DRAFT_PARAM } from '@utils/requisitionWizardPath';
import classes from './RequisitionWizard.module.css';
import { initialFeState as initialState, type RequisitionWizardState } from './RequisitionWizardState';
import { useAvvCodes } from './steps';
import { useContainerOrder } from './useContainerOrder';
import { type LinkNaming } from './useContainerSlot';
import { useAnsweredStepsSkipped } from './useAnsweredStepsSkipped';
import { usePrefilledRequisition } from './usePrefilledRequisition';
import { useRequisitionDraft } from './useRequisitionDraft';
import { useSiteStock } from './useSiteStock';

// @ai-generated 2026-07-03T20:10:00Z by dimitri.driediger@synqony.com using Claude Fable 5
export function RequisitionWizard() {
    const i18n = useTranslate('');
    const navigate = useClientNavigate();
    const [searchParams] = useSearchParams();
    const givenDraftId = searchParams.get(WIZARD_DRAFT_PARAM) ?? undefined;
    const { prefilled, reload } = usePrefilledRequisition(givenDraftId);
    const [committed, setCommitted] = useState<ApiRequisition | undefined>(undefined);

    useRemoteCacheInvalidation('requisitions', reload);

    const restart = useCallback(() => {
        setCommitted(undefined);
        reload();
    }, [reload]);

    if (committed) {
        return (
            <WizardSuccessScreen
                title={i18n('Bestellung eingegangen')}
                message={
                    <>
                        {i18n('Deine Bestellung')}{' '}
                        <Link to={`../${committed.id}`} relative="path" className={classes.orderNumber}>
                            {`${i18n('Nr.')} ${committed.number}`}
                        </Link>{' '}
                        {i18n('wurde erfolgreich übermittelt.')}
                    </>
                }
                hint={i18n(
                    'Du erhältst eine Bestätigung, sobald ein Termin eingeplant wurde. Den Status siehst du jederzeit unter Bestellungen.',
                )}
                secondaryAction={{ label: i18n('Weitere Bestellung'), onClick: restart }}
                primaryAction={{ label: i18n('Zur Übersicht'), onClick: () => navigate('/requisitions') }}
            />
        );
    }

    if (!prefilled) {
        return (
            <FlexColumn gap="none" height="100%" width="100%">
                <WizardHeader onBack={() => navigate(-1)} />
                <LoadingFrame />
            </FlexColumn>
        );
    }

    return (
        <RequisitionWizardInner
            key={prefilled.id}
            prefilled={prefilled}
            answered={prefilled.id === givenDraftId}
            onCommitted={setCommitted}
        />
    );
}

/** The wizard creates the order it is on, so it never offers an add action of its own. */
function WizardHeader({ onBack }: { onBack: () => void }) {
    const i18n = useTranslate('');
    const isPortalSession = useIsPortalSession();

    return (
        <FrameHeader
            label={i18n('Neue Bestellung')}
            resourceLabel="Bestellung"
            onBack={onBack}
            hideUnavailableActions={isPortalSession}
        />
    );
}

interface RequisitionWizardInnerProps {
    prefilled: ApiRequisition;
    /** Whether the draft arrived answered, so the wizard walks on to the first step it leaves open. */
    answered: boolean;
    onCommitted: (committed: ApiRequisition) => void;
}

// @ai-generated 2026-07-03T20:10:00Z by dimitri.driediger@synqony.com using Claude Fable 5
function RequisitionWizardInner({ prefilled, answered, onCommitted }: RequisitionWizardInnerProps) {
    const i18n = useTranslate('');
    const navigate = useClientNavigate();
    const apiResourcePath = `${AUFTRAG_API}/requisitions`;

    const { frameProps, submit, hasUnsavedChangesRef } = useRequisitionDraft({
        prefilled,
        apiResourcePath,
        onCommitted,
    });

    const shouldBlockNavigation = useCallback(
        ({ currentLocation, nextLocation }: { currentLocation: Location; nextLocation: Location }) => {
            if (currentLocation.pathname === nextLocation.pathname) {
                return false;
            }
            return hasUnsavedChangesRef.current;
        },
        [hasUnsavedChangesRef],
    );

    const blocker = useBlocker(shouldBlockNavigation);

    useBeforeUnload(
        useCallback(
            (event: BeforeUnloadEvent) => {
                if (hasUnsavedChangesRef.current) {
                    event.preventDefault();
                }
            },
            [hasUnsavedChangesRef],
        ),
    );

    const confirmLeave = useCallback(() => {
        if (blocker.state === 'blocked') {
            hasUnsavedChangesRef.current = false;
            blocker.proceed();
        }
    }, [blocker, hasUnsavedChangesRef]);

    const cancelLeave = useCallback(() => {
        if (blocker.state === 'blocked') {
            blocker.reset();
        }
    }, [blocker]);

    // Reset the dirty flag on unmount so a parent that swaps the wizard out
    // doesn't strand a stale "you have unsaved changes" prompt.
    useEffect(() => {
        return () => {
            hasUnsavedChangesRef.current = false;
        };
    }, [hasUnsavedChangesRef]);

    return (
        <FlexColumn gap="none" height="100%" width="100%">
            <WizardHeader onBack={() => navigate(-1)} />
            <ScrollContainer width="100%" height="100%">
                <RequisitionWizardBody
                    frameProps={frameProps}
                    answered={answered}
                    onSubmit={submit}
                    onCancel={() => navigate(-1)}
                    i18n={i18n}
                />
            </ScrollContainer>
            <UnsavedChangesModal isOpen={blocker.state === 'blocked'} onConfirm={confirmLeave} onClose={cancelLeave} />
        </FlexColumn>
    );
}

// @ai-generated 2026-07-03T20:10:00Z by dimitri.driediger@synqony.com using Claude Fable 5
interface RequisitionWizardBodyProps {
    frameProps: IFrameBodyProps<ApiRequisition>;
    answered: boolean;
    onSubmit: (data: RequisitionWizardState) => Promise<void>;
    onCancel: () => void;
    i18n: (key: string) => string;
}

/**
 * The steps of the wizard, and the state the container-aware ones share.
 *
 * With the container service switched off this is the three-step material wizard it has always
 * been. With it on, the action the customer takes sits between site and details, and decides
 * what the third step asks — so the action and its positions live here rather than in one step.
 */
// @ai-generated 2026-09-04T08:07:37+00:00 by dimitri.driediger@synqony.com using Claude Opus 5
function RequisitionWizardBody({
    frameProps: { data, meta, apiResourcePath, mode, requestUpdateState },
    answered,
    onSubmit,
    onCancel,
    i18n,
}: RequisitionWizardBodyProps) {
    const canEdit = mode !== EditFrameMode.View;
    const withContainers = FEATURE_FLAGS.CONTAINER_SERVICE;

    const avv = useAvvCodes();

    const { ensure: ensureTypeNames, labelOf: containerTypeOf } = usePortalContainerTypeLabels();
    const { ensure: ensureMaterialNames, labelOf: materialOf } = usePortalServiceLabels();
    const naming = useMemo<LinkNaming>(() => ({ containerTypeOf, materialOf }), [containerTypeOf, materialOf]);

    // Both the tile pill and the stock list read the same stock, so it is read once here. With
    // the flag off the container routes answer 404, so nothing is asked for in the first place.
    const {
        stock,
        total: stockTotal,
        containerTypes: stockTypes,
    } = useSiteStock({
        siteId: withContainers ? data.site.address.foreign_key : undefined,
        customerId: data.customer.foreign_key,
        containerTypeOf,
    });
    const stockLines = stock.lines;

    const { container_actions: containerActions, positions } = data;
    useEffect(() => {
        ensureTypeNames([...stockTypes, ...containerActions.map((action) => action.container_type)]);
    }, [stockTypes, containerActions, ensureTypeNames]);

    useEffect(() => {
        ensureMaterialNames(positions.map((position) => position.service));
    }, [positions, ensureMaterialNames]);

    const {
        action,
        slots,
        positions: containerPositions,
        positionCount,
        chooseAction,
        takeStockLine,
        removeFirst,
        removePosition,
    } = useContainerOrder({
        data,
        apiResourcePath,
        naming,
        stockLines,
        enabled: withContainers,
        canEdit,
        requestUpdateState,
    });

    // The waste code of what the customer picked. The picker itself no longer previews it, so it
    // is looked up for the services the order links and shown beside the fields.
    const [firstServiceId, secondServiceId] = [slots[0].contentField.linked_key, slots[1].contentField.linked_key];
    const { ensureCodes } = avv;
    useEffect(() => {
        ensureCodes([firstServiceId, secondServiceId].filter((id): id is string => !!id));
    }, [firstServiceId, secondServiceId, ensureCodes]);
    const avvCodes = useMemo<readonly [string, string]>(
        () => [
            (firstServiceId && avv.codes.get(firstServiceId)) || '',
            (secondServiceId && avv.codes.get(secondServiceId)) || '',
        ],
        [firstServiceId, secondServiceId, avv.codes],
    );

    /**
     * What switching the action would throw away.
     *
     * A container order keeps its one container on the container action, a loose-material order its
     * positions on the requisition — so which of the two counts depends on the action standing.
     * Without this a switch between the two kinds would discard silently.
     */
    const discardableCount =
        action === 'material' ? data.positions.filter((entry) => isLinked(entry.service)).length : positionCount;

    const steps = useMemo<WizardStep<RequisitionWizardState>[]>(
        () =>
            buildWizardSteps({
                data,
                meta,
                apiResourcePath,
                canEdit,
                withContainers,
                action,
                slots,
                positions: containerPositions,
                positionCount,
                discardableCount,
                stock,
                stockTotal,
                avvCodes,
                onChooseAction: chooseAction,
                onTakeLine: takeStockLine,
                onRemoveFirst: removeFirst,
                onRemovePosition: removePosition,
                requestUpdateState,
                i18n,
            }),
        [
            data,
            meta,
            apiResourcePath,
            canEdit,
            withContainers,
            action,
            slots,
            containerPositions,
            positionCount,
            discardableCount,
            stock,
            stockTotal,
            avvCodes,
            chooseAction,
            takeStockLine,
            removeFirst,
            removePosition,
            requestUpdateState,
            i18n,
        ],
    );

    const shownSteps = useAnsweredStepsSkipped(steps, answered);
    // The site of an answered draft is chosen, and shows as chosen when the customer returns to it.
    const initialData = useMemo(() => (answered ? { ...initialState, sitePicking: false } : initialState), [answered]);

    const detailsStepIndex = detailsStepIndexFor(withContainers);

    /** Appends what the current step has collected so far, e.g. "· 2 gewählt". */
    const stepInfoFormat = useCallback(
        (current: number, total: number, label: string) => {
            const base = `${i18n('Schritt')} ${current} ${i18n('von')} ${total} · ${label}`;
            if (!withContainers || current !== detailsStepIndex + 1) {
                return base;
            }
            if (countsSelection(action, stock.lines.length > 0)) {
                return `${base} · ${positionCount} ${i18n('gewählt')}`;
            }
            const count = action === 'material' ? data.positions.length : positionCount;
            return `${base} · ${count} ${count === 1 ? i18n('Position') : i18n('Positionen')}`;
        },
        [withContainers, action, positionCount, data.positions.length, stock.lines.length, detailsStepIndex, i18n],
    );

    return (
        <Wizard<RequisitionWizardState>
            steps={shownSteps}
            initialData={initialData}
            onSubmit={onSubmit}
            onCancel={onCancel}
            stepInfoFormat={stepInfoFormat}
            labels={{ submit: i18n('Bestellung abschicken') }}
        />
    );
}
